import { CostData, AggregatedData, DailyCostData, UserCostData, GroupData, GroupMapping } from '../types';
import { getCostDataSync, fetchCostDataFromApi } from './dataAdapter';
import groupData from '../data/group.json';

// 전역 데이터 저장소 (기존 코드와의 호환성 유지)
let globalData: CostData[] = [];
let isDataLoaded = false;
let isLoading = false;
let loadError: string | null = null;

// globalData에 직접 접근할 수 있는 함수 추가
export const getGlobalData = (): CostData[] => globalData;

// 그룹 데이터 초기화 함수 추가
export const initializeGroupsFromJson = (): void => {
  try {
    console.log('📁 group.json에서 그룹 데이터 로드 중...');
    
    // 기존 로컬스토리지 데이터 확인
    const existingGroups = loadGroupsFromStorage();
    console.log('🔍 기존 로컬스토리지 그룹 데이터:', existingGroups.length, '개 그룹');
    
    // group.json 데이터를 로컬스토리지에 저장
    saveGroupsToStorage(groupData);
    
    console.log('✅ 그룹 데이터 로컬스토리지 갱신 완료:', groupData.length, '개 그룹');
    
    // 변경 사항 로그
    if (existingGroups.length !== groupData.length) {
      console.log('📊 그룹 개수 변경:', existingGroups.length, '→', groupData.length);
    }
  } catch (error) {
    console.error('❌ 그룹 데이터 초기화 실패:', error);
  }
};

// 그룹 데이터 수동 재초기화 함수
export const refreshGroupsFromJson = (): void => {
  console.log('🔄 그룹 데이터 수동 재초기화 시작...');
  initializeGroupsFromJson();
};

// 앱 초기화 시 호출할 함수 (그룹 데이터 초기화 추가)
export const initializeData = async (): Promise<void> => {
  if (isDataLoaded || isLoading) {
    console.log('🔄 데이터 이미 로드됨 또는 로딩 중:', { isDataLoaded, isLoading });
    return;
  }
  
  isLoading = true;
  loadError = null;
  
  try {
    // 그룹 데이터 초기화 (group.json에서 로컬스토리지로)
    initializeGroupsFromJson();
    
    console.log('🚀 API에서 데이터 로드 중...');
    globalData = await fetchCostDataFromApi();
    isDataLoaded = true;
    console.log('✅ API 데이터 로드 완료:', globalData.length, '개 항목');
  } catch (error) {
    console.error('❌ API 로드 실패, 로컬 데이터 사용:', error);
    loadError = error instanceof Error ? error.message : 'Unknown error';
    // 로컬 데이터를 사용하도록 수정
    globalData = getCostDataSync() as CostData[];
    isDataLoaded = true;
    console.log('📁 로컬 데이터 사용:', globalData.length, '개 항목');
  } finally {
    isLoading = false;
  }
};

// 데이터 상태 확인 함수들
export const getDataLoadStatus = () => ({
  isLoaded: isDataLoaded,
  isLoading: isLoading,
  error: loadError,
  dataCount: globalData.length
});

// 수동 데이터 새로고침
export const refreshData = async (): Promise<void> => {
  isDataLoaded = false;
  await initializeData();
};

// 기존 data export를 getter 함수로 변경 (호환성 유지)
export const getData = (): CostData[] => globalData;

// 호환성을 위한 data 속성 (getter 사용)
export const data = getData();

export const getProviders = (): string[] => {
  return Array.from(new Set(globalData.map(item => item.provider)));
};

export const getDateRange = (): { startDate: string; endDate: string } => {
  if (globalData.length === 0) {
    return { startDate: '', endDate: '' };
  }
  const dates = globalData.map(item => item.date).sort();
  return {
    startDate: dates[0],
    endDate: dates[dates.length - 1]
  };
};

export const getTotalCosts = (dateRange?: { start: string; end: string }): AggregatedData[] => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  const providers = getProviders();
  
  return providers.map(provider => {
    const providerData = filteredData.filter(item => item.provider === provider);
    const totalCost = providerData.reduce((sum, item) => sum + item.cost_usd, 0);
    const uniqueUsers = new Set(providerData.map(item => item.identifier));
    
    return {
      provider,
      totalCost,
      userCount: uniqueUsers.size,
      avgCostPerUser: totalCost / uniqueUsers.size || 0
    };
  });
};

export const getDailyCosts = (dateRange?: { start: string; end: string }): DailyCostData[] => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  const dailyData: { [date: string]: { [provider: string]: number } } = {};
  
  filteredData.forEach(item => {
    if (!dailyData[item.date]) {
      dailyData[item.date] = {};
    }
    
    if (!dailyData[item.date][item.provider]) {
      dailyData[item.date][item.provider] = 0;
    }
    
    dailyData[item.date][item.provider] += item.cost_usd;
  });
  
  return Object.entries(dailyData)
    .map(([date, providers]) => {
      const totalCost = Object.values(providers).reduce((sum, cost) => sum + cost, 0);
      return {
        date,
        totalCost,
        ...providers
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

export const getTopUsers = (limit: number = 10, dateRange?: { start: string; end: string }): UserCostData[] => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  const userData: { [identifier: string]: UserCostData } = {};
  
  filteredData.forEach(item => {
    if (!userData[item.identifier]) {
      userData[item.identifier] = {
        identifier: item.identifier,
        totalCost: 0,
        providers: {}
      };
    }
    
    userData[item.identifier].totalCost += item.cost_usd;
    
    if (!userData[item.identifier].providers[item.provider]) {
      userData[item.identifier].providers[item.provider] = 0;
    }
    
    userData[item.identifier].providers[item.provider] += item.cost_usd;
  });
  
  return Object.values(userData)
    .sort((a, b) => b.totalCost - a.totalCost)
    .slice(0, limit);
};

export const getGroupedData = (groupMappings: GroupMapping): GroupData[] => {
  const groupData: { [groupName: string]: GroupData } = {};
  
  globalData.forEach(item => {
    const groupName = groupMappings[item.identifier] || 'Ungrouped';
    
    if (!groupData[groupName]) {
      groupData[groupName] = {
        name: groupName,
        totalCost: 0,
        userCount: 0,
        providers: {}
      };
    }
    
    groupData[groupName].totalCost += item.cost_usd;
    
    if (!groupData[groupName].providers[item.provider]) {
      groupData[groupName].providers[item.provider] = 0;
    }
    
    groupData[groupName].providers[item.provider] += item.cost_usd;
  });
  
  // Count unique users per group
  Object.keys(groupData).forEach(groupName => {
    const groupUsers = new Set();
    globalData.forEach(item => {
      const itemGroup = groupMappings[item.identifier] || 'Ungrouped';
      if (itemGroup === groupName) {
        groupUsers.add(item.identifier);
      }
    });
    groupData[groupName].userCount = groupUsers.size;
  });
  
  return Object.values(groupData)
    .sort((a, b) => b.totalCost - a.totalCost);
};

export const getOverallMetrics = (dateRange?: { start: string; end: string }) => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  const totalCost = filteredData.reduce((sum, item) => sum + item.cost_usd, 0);
  const uniqueUsers = new Set(filteredData.map(item => item.identifier)).size;
  const uniqueProviders = new Set(filteredData.map(item => item.provider)).size;
  const avgCostPerUser = totalCost / uniqueUsers || 0;
  
  return {
    totalCost,
    uniqueUsers,
    uniqueProviders,
    avgCostPerUser
  };
};

// Groups 페이지를 위한 새로운 함수들
export const calculateGroupCost = (members: any[], dateRange?: { start: string; end: string }): number => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  console.log('💰 그룹 비용 계산 시작:', {
    membersCount: members.length,
    filteredDataCount: filteredData.length,
    sampleMembers: members.slice(0, 3),
    sampleData: filteredData.slice(0, 3).map(item => ({
      provider: item.provider,
      identifier: item.identifier,
      cost_usd: item.cost_usd,
      date: item.date
    }))
  });
  
  let totalCost = 0;
  let matchedItems = 0;
  
  filteredData.forEach(item => {
    const memberExists = members.some(
      member => {
        const matches = member.provider === item.provider && member.identifier === item.identifier;
        if (matches) {
          console.log('✅ 매칭 성공:', { 
            member: { provider: member.provider, identifier: member.identifier },
            item: { provider: item.provider, identifier: item.identifier, cost: item.cost_usd }
          });
        }
        return matches;
      }
    );
    if (memberExists) {
      totalCost += item.cost_usd;
      matchedItems++;
    }
  });
  
  console.log('💰 그룹 비용 계산 결과:', {
    totalCost,
    matchedItems,
    totalItems: filteredData.length
  });
  
  return totalCost;
};

export const getGroupsWithCosts = (dateRange?: { start: string; end: string }) => {
  const savedGroups = localStorage.getItem('groups');
  if (!savedGroups) {
    return [];
  }
  
  const parsedGroups = JSON.parse(savedGroups);
  return parsedGroups.map((group: any) => ({
    ...group,
    totalCost: calculateGroupCost(group.members, dateRange)
  }));
};

export const getGroupPieData = (groups: any[]) => {
  const totalCost = groups.reduce((sum, group) => sum + group.totalCost, 0);
  
  const colors = [
    { bg: 'bg-blue-500', text: 'text-blue-500', stroke: '#3B82F6' },
    { bg: 'bg-green-500', text: 'text-green-500', stroke: '#10B981' },
    { bg: 'bg-purple-500', text: 'text-purple-500', stroke: '#8B5CF6' },
    { bg: 'bg-yellow-500', text: 'text-yellow-500', stroke: '#F59E0B' },
    { bg: 'bg-red-500', text: 'text-red-500', stroke: '#EF4444' },
    { bg: 'bg-indigo-500', text: 'text-indigo-500', stroke: '#6366F1' },
    { bg: 'bg-pink-500', text: 'text-pink-500', stroke: '#EC4899' },
    { bg: 'bg-teal-500', text: 'text-teal-500', stroke: '#14B8A6' }
  ];
  
  return groups.map((group, index) => {
    const percentage = totalCost > 0 ? (group.totalCost / totalCost) * 100 : 0;
    return {
      ...group,
      percentage,
      color: colors[index % colors.length]
    };
  });
};

export const getGroupTrendData = (groups: any[], dateRange?: { start: string; end: string }, showSeparately: boolean = false) => {
  console.log('📊 getGroupTrendData 호출:', { 
    groupsLength: groups.length, 
    dateRange, 
    showSeparately,
    globalDataLength: globalData.length 
  });
  
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
    console.log('📅 날짜 필터링된 데이터:', filteredData.length, '개 항목');
  }
  
  console.log('👥 그룹 정보:', groups.map(g => ({ id: g.id, name: g.name, membersCount: g.members?.length || 0 })));
  
  if (showSeparately) {
    // 각 그룹별 데이터 생성
    const groupTrendData: { [key: string]: { [key: string]: number } } = {};
    const allDates = new Set<string>();
    
    groups.forEach(group => {
      groupTrendData[group.id] = {};
      console.log(`🔍 그룹 ${group.name} 데이터 처리 중...`);
      
      filteredData.forEach(item => {
        const isGroupMember = group.members.some((member: any) => 
          member.provider === item.provider && member.identifier === item.identifier
        );
        if (isGroupMember && item.date) {
          groupTrendData[group.id][item.date] = (groupTrendData[group.id][item.date] || 0) + item.cost_usd;
          allDates.add(item.date);
        }
      });
      
      console.log(`📈 그룹 ${group.name} 일별 데이터:`, Object.keys(groupTrendData[group.id]).length, '개 날짜');
    });
    
    // 모든 날짜에 대해 각 그룹의 데이터를 정리
    const sortedDates = Array.from(allDates).sort();
    console.log('📅 전체 날짜 범위:', sortedDates.length, '개 날짜', sortedDates.slice(0, 5));
    
    const result = sortedDates.map(date => {
      const dataPoint: any = { date };
      groups.forEach(group => {
        dataPoint[group.id] = groupTrendData[group.id][date] || 0;
      });
      return dataPoint;
    });
    
    console.log('📊 개별 그룹 차트 데이터:', result.length, '개 포인트');
    return result;
  } else {
    // 전체 합계 트렌드
    const dailyData: { [key: string]: number } = {};
    
    filteredData.forEach(item => {
      if (groups.length > 0) {
        const isGroupMember = groups.some(group => 
          group.members.some((member: any) => 
            member.provider === item.provider && member.identifier === item.identifier
          )
        );
        if (isGroupMember && item.date) {
          dailyData[item.date] = (dailyData[item.date] || 0) + item.cost_usd;
        }
      }
    });
    
    const sortedDates = Object.keys(dailyData).sort();
    console.log('📅 통합 차트 날짜:', sortedDates.length, '개 날짜');
    console.log('💰 일별 비용 샘플:', Object.entries(dailyData).slice(0, 5));
    
    const result = sortedDates.map(date => ({
      date,
      totalCost: dailyData[date] || 0
    }));
    
    console.log('📊 통합 차트 데이터:', result.length, '개 포인트');
    return result;
  }
};

export const getGroupTrendDataForTable = (group: any, dateRange?: { start: string; end: string }): number[] => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  const dailyData: { [key: string]: number } = {};
  
  filteredData.forEach(dataItem => {
    const isGroupMember = group.members.some((member: any) => 
      member.provider === dataItem.provider && member.identifier === dataItem.identifier
    );
    if (isGroupMember && dataItem.date) {
      dailyData[dataItem.date] = (dailyData[dataItem.date] || 0) + dataItem.cost_usd;
    }
  });
  
  const sortedDates = Object.keys(dailyData).sort();
  return sortedDates.map(date => dailyData[date] || 0);
};

// Users 페이지를 위한 새로운 함수들
export const calculateUserCost = (providers: any[], dateRange?: { start: string; end: string }): number => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  return filteredData.reduce((total, item) => {
    const providerExists = providers.some(
      provider => provider.provider === item.provider && provider.identifier === item.identifier
    );
    return providerExists ? total + item.cost_usd : total;
  }, 0);
};

export const getUsersWithCosts = (dateRange?: { start: string; end: string }) => {
  const savedUsers = localStorage.getItem('users');
  if (!savedUsers) {
    return [];
  }
  
  const parsedUsers = JSON.parse(savedUsers);
  return parsedUsers.map((user: any) => ({
    ...user,
    totalCost: calculateUserCost(user.providers, dateRange)
  }));
};

export const getUserPieData = (users: any[]) => {
  const totalCost = users.reduce((sum, user) => sum + user.totalCost, 0);
  
  const colors = [
    { bg: 'bg-blue-500', text: 'text-blue-500', stroke: '#3B82F6' },
    { bg: 'bg-green-500', text: 'text-green-500', stroke: '#10B981' },
    { bg: 'bg-purple-500', text: 'text-purple-500', stroke: '#8B5CF6' },
    { bg: 'bg-yellow-500', text: 'text-yellow-500', stroke: '#F59E0B' },
    { bg: 'bg-red-500', text: 'text-red-500', stroke: '#EF4444' },
    { bg: 'bg-indigo-500', text: 'text-indigo-500', stroke: '#6366F1' },
    { bg: 'bg-pink-500', text: 'text-pink-500', stroke: '#EC4899' },
    { bg: 'bg-teal-500', text: 'text-teal-500', stroke: '#14B8A6' }
  ];
  
  return users.map((user, index) => {
    const percentage = totalCost > 0 ? (user.totalCost / totalCost) * 100 : 0;
    return {
      ...user,
      percentage,
      color: colors[index % colors.length]
    };
  });
};

export const getUserTrendData = (users: any[], dateRange?: { start: string; end: string }, showSeparately: boolean = false) => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  if (showSeparately) {
    // 각 사용자별 데이터 생성
    const userTrendData: { [key: string]: { [key: string]: number } } = {};
    const allDates = new Set<string>();
    
    users.forEach(user => {
      userTrendData[user.id] = {};
      
      filteredData.forEach(item => {
        const isUserProvider = user.providers.some((provider: any) => 
          provider.provider === item.provider && provider.identifier === item.identifier
        );
        if (isUserProvider && item.date) {
          userTrendData[user.id][item.date] = (userTrendData[user.id][item.date] || 0) + item.cost_usd;
          allDates.add(item.date);
        }
      });
    });
    
    // 모든 날짜에 대해 각 사용자의 데이터를 정리
    const sortedDates = Array.from(allDates).sort();
    return sortedDates.map(date => {
      const dataPoint: any = { date };
      users.forEach(user => {
        dataPoint[user.id] = userTrendData[user.id][date] || 0;
      });
      return dataPoint;
    });
  } else {
    // 전체 합계 트렌드
    const dailyData: { [key: string]: number } = {};
    
    filteredData.forEach(item => {
      if (users.length > 0) {
        const isUserProvider = users.some(user => 
          user.providers.some((provider: any) => 
            provider.provider === item.provider && provider.identifier === item.identifier
          )
        );
        if (isUserProvider && item.date) {
          dailyData[item.date] = (dailyData[item.date] || 0) + item.cost_usd;
        }
      }
    });
    
    const sortedDates = Object.keys(dailyData).sort();
    return sortedDates.map(date => ({
      date,
      totalCost: dailyData[date] || 0
    }));
  }
};

export const getUserTrendDataForTable = (user: any, dateRange?: { start: string; end: string }): number[] => {
  let filteredData = globalData;
  
  if (dateRange) {
    filteredData = globalData.filter(item => 
      item.date >= dateRange.start && item.date <= dateRange.end
    );
  }
  
  const dailyData: { [key: string]: number } = {};
  
  filteredData.forEach(dataItem => {
    const isUserProvider = user.providers.some((provider: any) => 
      provider.provider === dataItem.provider && provider.identifier === dataItem.identifier
    );
    if (isUserProvider && dataItem.date) {
      dailyData[dataItem.date] = (dailyData[dataItem.date] || 0) + dataItem.cost_usd;
    }
  });
  
  const sortedDates = Object.keys(dailyData).sort();
  return sortedDates.map(date => dailyData[date] || 0);
};

// Settings 페이지를 위한 새로운 함수들
export const getAvailableMembers = (): { provider: string; identifier: string }[] => {
  const uniqueMembers: { provider: string; identifier: string }[] = [];
  const seen = new Set<string>();
  
  globalData.forEach(item => {
    const key = `${item.provider}|${item.identifier}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueMembers.push({ provider: item.provider, identifier: item.identifier });
    }
  });
  
  return uniqueMembers;
};

export const loadGroupsFromStorage = () => {
  const savedGroups = localStorage.getItem('groups');
  if (savedGroups) {
    return JSON.parse(savedGroups);
  }
  return [];
};

export const saveGroupsToStorage = (groups: any[]): void => {
  try {
    localStorage.setItem('groups', JSON.stringify(groups));
  } catch (err) {
    console.error('Failed to save groups:', err);
    throw new Error('그룹 저장에 실패했습니다. 다시 시도해주세요.');
  }
};

export const loadUsersFromStorage = () => {
  const savedUsers = localStorage.getItem('users');
  if (savedUsers) {
    return JSON.parse(savedUsers);
  }
  return [];
};

export const saveUsersToStorage = (users: any[]): void => {
  try {
    localStorage.setItem('users', JSON.stringify(users));
  } catch (err) {
    console.error('Failed to save users:', err);
    throw new Error('사용자 저장에 실패했습니다. 다시 시도해주세요.');
  }
};

export const generateUniqueId = (prefix: string = 'item'): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// ✨ 새로운 함수들 - React Query 데이터를 받아서 처리
export const getTotalCostsFromData = (data: CostData[]): AggregatedData[] => {
  const providers = Array.from(new Set(data.map(item => item.provider)));
  
  return providers.map(provider => {
    const providerData = data.filter(item => item.provider === provider);
    const totalCost = providerData.reduce((sum, item) => sum + item.cost_usd, 0);
    const uniqueUsers = new Set(providerData.map(item => item.identifier));
    
    return {
      provider,
      totalCost,
      userCount: uniqueUsers.size,
      avgCostPerUser: totalCost / uniqueUsers.size || 0
    };
  });
};

export const getDailyCostsFromData = (data: CostData[]): DailyCostData[] => {
  const dailyData: { [date: string]: { [provider: string]: number } } = {};
  
  data.forEach(item => {
    if (!dailyData[item.date]) {
      dailyData[item.date] = {};
    }
    
    if (!dailyData[item.date][item.provider]) {
      dailyData[item.date][item.provider] = 0;
    }
    
    dailyData[item.date][item.provider] += item.cost_usd;
  });
  
  return Object.entries(dailyData)
    .map(([date, providers]) => {
      const totalCost = Object.values(providers).reduce((sum, cost) => sum + cost, 0);
      return {
        date,
        totalCost,
        ...providers
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
};

export const getOverallMetricsFromData = (data: CostData[]) => {
  const totalCost = data.reduce((sum, item) => sum + item.cost_usd, 0);
  const uniqueUsers = new Set(data.map(item => item.identifier)).size;
  const uniqueProviders = new Set(data.map(item => item.provider)).size;
  const avgCostPerUser = totalCost / uniqueUsers || 0;
  
  return {
    totalCost,
    uniqueUsers,
    uniqueProviders,
    avgCostPerUser
  };
};

export const getTopUsersFromData = (data: CostData[], limit: number = 10): UserCostData[] => {
  const userData: { [identifier: string]: UserCostData } = {};
  
  data.forEach(item => {
    if (!userData[item.identifier]) {
      userData[item.identifier] = {
        identifier: item.identifier,
        totalCost: 0,
        providers: {}
      };
    }
    
    userData[item.identifier].totalCost += item.cost_usd;
    
    if (!userData[item.identifier].providers[item.provider]) {
      userData[item.identifier].providers[item.provider] = 0;
    }
    
    userData[item.identifier].providers[item.provider] += item.cost_usd;
  });
  
  return Object.values(userData)
    .sort((a, b) => b.totalCost - a.totalCost)
    .slice(0, limit);
};

export const getUsersWithCostsFromData = (data: CostData[]) => {
  const savedUsers = localStorage.getItem('users');
  if (!savedUsers) {
    return [];
  }
  
  const parsedUsers = JSON.parse(savedUsers);
  return parsedUsers.map((user: any) => ({
    ...user,
    totalCost: calculateUserCostFromData(user.providers, data)
  }));
};

export const getGroupsWithCostsFromData = (data: CostData[]) => {
  const savedGroups = localStorage.getItem('groups');
  if (!savedGroups) {
    return [];
  }
  
  const parsedGroups = JSON.parse(savedGroups);
  return parsedGroups.map((group: any) => ({
    ...group,
    totalCost: calculateGroupCostFromData(group.members, data)
  }));
};

const calculateUserCostFromData = (providers: any[], data: CostData[]): number => {
  return data.reduce((total, item) => {
    const providerExists = providers.some(
      provider => provider.provider === item.provider && provider.identifier === item.identifier
    );
    return providerExists ? total + item.cost_usd : total;
  }, 0);
};

const calculateGroupCostFromData = (members: any[], data: CostData[]): number => {
  let totalCost = 0;
  let matchedItems = 0;
  
  data.forEach(item => {
    const memberExists = members.some(
      member => member.provider === item.provider && member.identifier === item.identifier
    );
    if (memberExists) {
      totalCost += item.cost_usd;
      matchedItems++;
    }
  });
  
  console.log('💰 그룹 비용 계산 결과 (새 함수):', {
    totalCost,
    matchedItems,
    totalItems: data.length
  });
  
  return totalCost;
};

// ✨ Users 페이지용 새로운 함수들 추가
export const getUserPieDataFromData = (data: CostData[]) => {
  const users = getUsersWithCostsFromData(data);
  const totalCost = users.reduce((sum: number, user: any) => sum + user.totalCost, 0);
  
  const colors = [
    { bg: 'bg-blue-500', text: 'text-blue-500', stroke: '#3B82F6' },
    { bg: 'bg-green-500', text: 'text-green-500', stroke: '#10B981' },
    { bg: 'bg-purple-500', text: 'text-purple-500', stroke: '#8B5CF6' },
    { bg: 'bg-yellow-500', text: 'text-yellow-500', stroke: '#F59E0B' },
    { bg: 'bg-red-500', text: 'text-red-500', stroke: '#EF4444' },
    { bg: 'bg-indigo-500', text: 'text-indigo-500', stroke: '#6366F1' },
    { bg: 'bg-pink-500', text: 'text-pink-500', stroke: '#EC4899' },
    { bg: 'bg-teal-500', text: 'text-teal-500', stroke: '#14B8A6' }
  ];
  
  return users.map((user: any, index: number) => {
    const percentage = totalCost > 0 ? (user.totalCost / totalCost) * 100 : 0;
    return {
      ...user,
      percentage,
      color: colors[index % colors.length]
    };
  });
};

export const getUserTrendDataFromData = (data: CostData[], showSeparately: boolean = false) => {
  const users = getUsersWithCostsFromData(data);
  
  if (showSeparately) {
    // 각 사용자별 데이터 생성
    const userTrendData: { [key: string]: { [key: string]: number } } = {};
    const allDates = new Set<string>();
    
    users.forEach((user: any) => {
      userTrendData[user.id] = {};
      
      data.forEach(item => {
        const isUserProvider = user.providers.some((provider: any) => 
          provider.provider === item.provider && provider.identifier === item.identifier
        );
        if (isUserProvider && item.date) {
          userTrendData[user.id][item.date] = (userTrendData[user.id][item.date] || 0) + item.cost_usd;
          allDates.add(item.date);
        }
      });
    });
    
    // 모든 날짜에 대해 각 사용자의 데이터를 정리
    const sortedDates = Array.from(allDates).sort();
    return sortedDates.map(date => {
      const dataPoint: any = { date };
      users.forEach((user: any) => {
        dataPoint[user.id] = userTrendData[user.id][date] || 0;
      });
      return dataPoint;
    });
  } else {
    // 전체 합계 트렌드
    const dailyData: { [key: string]: number } = {};
    
    data.forEach(item => {
      if (users.length > 0) {
        const isUserProvider = users.some((user: any) => 
          user.providers.some((provider: any) => 
            provider.provider === item.provider && provider.identifier === item.identifier
          )
        );
        if (isUserProvider && item.date) {
          dailyData[item.date] = (dailyData[item.date] || 0) + item.cost_usd;
        }
      }
    });
    
    const sortedDates = Object.keys(dailyData).sort();
    return sortedDates.map(date => ({
      date,
      totalCost: dailyData[date] || 0
    }));
  }
};

export const getUserTrendDataForTableFromData = (user: any, data: CostData[]): number[] => {
  const dailyData: { [key: string]: number } = {};
  
  data.forEach(dataItem => {
    const isUserProvider = user.providers.some((provider: any) => 
      provider.provider === dataItem.provider && provider.identifier === dataItem.identifier
    );
    if (isUserProvider && dataItem.date) {
      dailyData[dataItem.date] = (dailyData[dataItem.date] || 0) + dataItem.cost_usd;
    }
  });
  
  const sortedDates = Object.keys(dailyData).sort();
  return sortedDates.map(date => dailyData[date] || 0);
};

// ✨ Groups 페이지용 새로운 함수들 추가
export const getGroupPieDataFromData = (data: CostData[]) => {
  const groups = getGroupsWithCostsFromData(data);
  const totalCost = groups.reduce((sum: number, group: any) => sum + group.totalCost, 0);
  
  const colors = [
    { bg: 'bg-blue-500', text: 'text-blue-500', stroke: '#3B82F6' },
    { bg: 'bg-green-500', text: 'text-green-500', stroke: '#10B981' },
    { bg: 'bg-purple-500', text: 'text-purple-500', stroke: '#8B5CF6' },
    { bg: 'bg-yellow-500', text: 'text-yellow-500', stroke: '#F59E0B' },
    { bg: 'bg-red-500', text: 'text-red-500', stroke: '#EF4444' },
    { bg: 'bg-indigo-500', text: 'text-indigo-500', stroke: '#6366F1' },
    { bg: 'bg-pink-500', text: 'text-pink-500', stroke: '#EC4899' },
    { bg: 'bg-teal-500', text: 'text-teal-500', stroke: '#14B8A6' }
  ];
  
  return groups.map((group: any, index: number) => {
    const percentage = totalCost > 0 ? (group.totalCost / totalCost) * 100 : 0;
    return {
      ...group,
      percentage,
      color: colors[index % colors.length]
    };
  });
};

export const getGroupTrendDataFromData = (data: CostData[], showSeparately: boolean = false) => {
  const groups = getGroupsWithCostsFromData(data);
  
  console.log('📊 getGroupTrendDataFromData 호출:', { 
    groupsLength: groups.length, 
    showSeparately,
    dataLength: data.length 
  });
  
  if (showSeparately) {
    // 각 그룹별 데이터 생성
    const groupTrendData: { [key: string]: { [key: string]: number } } = {};
    const allDates = new Set<string>();
    
    groups.forEach((group: any) => {
      groupTrendData[group.id] = {};
      console.log(`🔍 그룹 ${group.name} 데이터 처리 중...`);
      
      data.forEach(item => {
        const isGroupMember = group.members.some((member: any) => 
          member.provider === item.provider && member.identifier === item.identifier
        );
        if (isGroupMember && item.date) {
          groupTrendData[group.id][item.date] = (groupTrendData[group.id][item.date] || 0) + item.cost_usd;
          allDates.add(item.date);
        }
      });
      
      console.log(`📈 그룹 ${group.name} 일별 데이터:`, Object.keys(groupTrendData[group.id]).length, '개 날짜');
    });
    
    // 모든 날짜에 대해 각 그룹의 데이터를 정리
    const sortedDates = Array.from(allDates).sort();
    console.log('📅 전체 날짜 범위:', sortedDates.length, '개 날짜', sortedDates.slice(0, 5));
    
    const result = sortedDates.map(date => {
      const dataPoint: any = { date };
      groups.forEach((group: any) => {
        dataPoint[group.id] = groupTrendData[group.id][date] || 0;
      });
      return dataPoint;
    });
    
    console.log('📊 개별 그룹 차트 데이터:', result.length, '개 포인트');
    return result;
  } else {
    // 전체 합계 트렌드
    const dailyData: { [key: string]: number } = {};
    
    data.forEach(item => {
      if (groups.length > 0) {
        const isGroupMember = groups.some((group: any) => 
          group.members.some((member: any) => 
            member.provider === item.provider && member.identifier === item.identifier
          )
        );
        if (isGroupMember && item.date) {
          dailyData[item.date] = (dailyData[item.date] || 0) + item.cost_usd;
        }
      }
    });
    
    const sortedDates = Object.keys(dailyData).sort();
    console.log('📅 통합 차트 날짜:', sortedDates.length, '개 날짜');
    console.log('💰 일별 비용 샘플:', Object.entries(dailyData).slice(0, 5));
    
    const result = sortedDates.map(date => ({
      date,
      totalCost: dailyData[date] || 0
    }));
    
    console.log('📊 통합 차트 데이터:', result.length, '개 포인트');
    return result;
  }
};

export const getGroupTrendDataForTableFromData = (group: any, data: CostData[]): number[] => {
  const dailyData: { [key: string]: number } = {};
  
  data.forEach(dataItem => {
    const isGroupMember = group.members.some((member: any) => 
      member.provider === dataItem.provider && member.identifier === dataItem.identifier
    );
    if (isGroupMember && dataItem.date) {
      dailyData[dataItem.date] = (dailyData[dataItem.date] || 0) + dataItem.cost_usd;
    }
  });
  
  const sortedDates = Object.keys(dailyData).sort();
  return sortedDates.map(date => dailyData[date] || 0);
};